var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useEffect, useMemo, useState } from "react";
import { typeAlert } from "@affinitysystemsgroup/github-packages-affinity-components/dist";
import { useGetRejectionCodeSets } from "./useGetRejectionCodeSets";
import { useCreateRejectionCodeSet } from "./useCreateRejectionCodeSet";
import { useUpdateRejectionCodeSet } from "./useUpdateRejectionCodeSet";
import { useDisableRejectionCodeSet } from "./useDisableRejectionCodeSet";
import { useDeleteRejectionCodeSet } from "./useDeleteRejectionCodeSet";
import { useRejectionCodesErrorHandling } from "./useRejectionCodesErrorHandling";
import { REJECTION_CODES_TEXT } from "../constants/rejectionCodes.constants";
var INITIAL_SETS = [];
var PAGE_SIZE = 10;
var UPDATE_SUCCESS_MESSAGE = {
    rename: REJECTION_CODES_TEXT.renameSetSuccess,
    addCode: REJECTION_CODES_TEXT.addCodeSuccess,
    updateCode: REJECTION_CODES_TEXT.updateCodeSuccess,
    removeCode: REJECTION_CODES_TEXT.removeCodeSuccess,
};
var toCodesMap = function (codes) {
    return Object.fromEntries(codes.map(function (code) {
        var _a, _b;
        return [
            (_a = code.code) !== null && _a !== void 0 ? _a : "__EMPTY__",
            __assign(__assign(__assign({ label: (_b = code.label) !== null && _b !== void 0 ? _b : "", transaction: code.transaction, description: code.description }, (code.action ? { action: code.action } : {})), (code.actionDescription
                ? { actionDescription: code.actionDescription }
                : {})), (code.cancellationReasonId
                ? { cancellationReasonId: code.cancellationReasonId }
                : {})),
        ];
    }));
};
var mapDtoEntryToCode = function (key, entry) {
    var _a, _b, _c, _d;
    return ({
        id: key,
        code: key === "__EMPTY__" ? null : key,
        label: (_a = entry.label) !== null && _a !== void 0 ? _a : null,
        transaction: entry.transaction,
        description: entry.description,
        action: (_b = entry.action) !== null && _b !== void 0 ? _b : null,
        actionDescription: (_c = entry.actionDescription) !== null && _c !== void 0 ? _c : null,
        cancellationReasonId: (_d = entry.cancellationReasonId) !== null && _d !== void 0 ? _d : null,
    });
};
var mapDtoToSet = function (dto) { return ({
    id: dto.id,
    name: dto.name,
    shortCode: dto.code,
    enabled: dto.enabled === 1,
    clientId: dto.clientId,
    version: dto.version,
    codes: Object.entries(dto.codes).map(function (_a) {
        var key = _a[0], entry = _a[1];
        return mapDtoEntryToCode(key, entry);
    }),
}); };
var formValuesToEntry = function (values) { return (__assign(__assign({ label: values.label.trim(), transaction: values.transaction, description: values.description.trim() }, (values.action.trim() ? { action: values.action.trim() } : {})), (values.actionDescription.trim()
    ? { actionDescription: values.actionDescription.trim() }
    : {}))); };
export var useRejectionCodes = function () {
    var _a = useState(INITIAL_SETS), sets = _a[0], setSets = _a[1];
    var _b = useState(""), selectedSetId = _b[0], setSelectedSetId = _b[1];
    var _c = useState("all"), filter = _c[0], setFilter = _c[1];
    var _d = useState(1), page = _d[0], setPage = _d[1];
    var _e = useState(false), newSetModalOpen = _e[0], setNewSetModalOpen = _e[1];
    var _f = useState(false), renameModalOpen = _f[0], setRenameModalOpen = _f[1];
    var _g = useState(false), confirmDisableModalOpen = _g[0], setConfirmDisableModalOpen = _g[1];
    var _h = useState(false), confirmDeleteModalOpen = _h[0], setConfirmDeleteModalOpen = _h[1];
    var _j = useState({
        show: false,
        message: "",
        type: typeAlert.success,
    }), seeToast = _j[0], setSeeToast = _j[1];
    var _k = useGetRejectionCodeSets(), fetchRejectionCodeSets = _k.fetchRejectionCodeSets, rejectionCodeSetsResponse = _k.rejectionCodeSetsResponse, loadingRejectionCodeSets = _k.loadingRejectionCodeSets, errorRejectionCodeSets = _k.errorRejectionCodeSets;
    var _l = useCreateRejectionCodeSet(), createRejectionCodeSet = _l.createRejectionCodeSet, clearCreateRejectionCodeSet = _l.clearCreateRejectionCodeSet, createdRejectionCodeSet = _l.createdRejectionCodeSet, creatingRejectionCodeSet = _l.creatingRejectionCodeSet, errorCreateRejectionCodeSet = _l.errorCreateRejectionCodeSet;
    var _m = useUpdateRejectionCodeSet(), updateRejectionCodeSet = _m.updateRejectionCodeSet, clearUpdateRejectionCodeSet = _m.clearUpdateRejectionCodeSet, updatedRejectionCodeSet = _m.updatedRejectionCodeSet, updatingRejectionCodeSet = _m.updatingRejectionCodeSet, errorUpdateRejectionCodeSet = _m.errorUpdateRejectionCodeSet;
    var _o = useDisableRejectionCodeSet(), disableRejectionCodeSet = _o.disableRejectionCodeSet, clearDisableRejectionCodeSet = _o.clearDisableRejectionCodeSet, disabledRejectionCodeSet = _o.disabledRejectionCodeSet, disablingRejectionCodeSet = _o.disablingRejectionCodeSet, errorDisableRejectionCodeSet = _o.errorDisableRejectionCodeSet;
    var _p = useDeleteRejectionCodeSet(), deleteRejectionCodeSet = _p.deleteRejectionCodeSet, clearDeleteRejectionCodeSet = _p.clearDeleteRejectionCodeSet, deletedRejectionCodeSetResponse = _p.deletedRejectionCodeSetResponse, deletingRejectionCodeSet = _p.deletingRejectionCodeSet, errorDeleteRejectionCodeSet = _p.errorDeleteRejectionCodeSet;
    var _q = useState(""), pendingDeleteSetId = _q[0], setPendingDeleteSetId = _q[1];
    var _r = useState(null), updateIntent = _r[0], setUpdateIntent = _r[1];
    useEffect(function () {
        fetchRejectionCodeSets();
    }, []);
    useEffect(function () {
        if (!rejectionCodeSetsResponse)
            return;
        var mappedSets = rejectionCodeSetsResponse.map(mapDtoToSet);
        setSets(mappedSets);
        setSelectedSetId(function (prev) { var _a; return prev || ((_a = mappedSets[0]) === null || _a === void 0 ? void 0 : _a.id) || ""; });
    }, [rejectionCodeSetsResponse]);
    useEffect(function () {
        if (!createdRejectionCodeSet)
            return;
        setSelectedSetId(createdRejectionCodeSet.id);
        setNewSetModalOpen(false);
        clearCreateRejectionCodeSet();
        fetchRejectionCodeSets();
        setSeeToast({
            show: true,
            type: typeAlert.success,
            message: REJECTION_CODES_TEXT.createSetSuccess,
        });
    }, [createdRejectionCodeSet]);
    useEffect(function () {
        if (!updatedRejectionCodeSet)
            return;
        setRenameModalOpen(false);
        clearUpdateRejectionCodeSet();
        fetchRejectionCodeSets();
        setSeeToast({
            show: true,
            type: typeAlert.success,
            message: updateIntent
                ? UPDATE_SUCCESS_MESSAGE[updateIntent]
                : REJECTION_CODES_TEXT.renameSetSuccess,
        });
        setUpdateIntent(null);
    }, [updatedRejectionCodeSet]);
    useEffect(function () {
        if (!disabledRejectionCodeSet)
            return;
        setConfirmDisableModalOpen(false);
        clearDisableRejectionCodeSet();
        fetchRejectionCodeSets();
        setSeeToast({
            show: true,
            type: typeAlert.success,
            message: REJECTION_CODES_TEXT.disableSetSuccess,
        });
    }, [disabledRejectionCodeSet]);
    useEffect(function () {
        if (!deletedRejectionCodeSetResponse || !pendingDeleteSetId)
            return;
        setSelectedSetId(function (prev) { return (prev === pendingDeleteSetId ? "" : prev); });
        setPendingDeleteSetId("");
        setConfirmDeleteModalOpen(false);
        clearDeleteRejectionCodeSet();
        fetchRejectionCodeSets();
        setSeeToast({
            show: true,
            type: typeAlert.success,
            message: REJECTION_CODES_TEXT.deleteSetSuccess,
        });
    }, [deletedRejectionCodeSetResponse]);
    useRejectionCodesErrorHandling({
        errorRejectionCodeSets: errorRejectionCodeSets,
        errorCreateRejectionCodeSet: errorCreateRejectionCodeSet,
        errorUpdateRejectionCodeSet: errorUpdateRejectionCodeSet,
        errorDisableRejectionCodeSet: errorDisableRejectionCodeSet,
        errorDeleteRejectionCodeSet: errorDeleteRejectionCodeSet,
        onUpdateError: fetchRejectionCodeSets,
        onDeleteError: function () { return setPendingDeleteSetId(""); },
        setSeeToast: setSeeToast,
    });
    var filteredSets = useMemo(function () {
        if (filter === "enabled")
            return sets.filter(function (set) { return set.enabled; });
        if (filter === "disabled")
            return sets.filter(function (set) { return !set.enabled; });
        return sets;
    }, [sets, filter]);
    var totalItems = filteredSets.length;
    var paginatedSets = useMemo(function () { return filteredSets.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE); }, [filteredSets, page]);
    var onFilterChange = function (nextFilter) {
        setFilter(nextFilter);
        setPage(1);
    };
    var selectedSet = useMemo(function () { return sets.find(function (set) { return set.id === selectedSetId; }); }, [sets, selectedSetId]);
    var onCreateSet = function (values, codes) {
        createRejectionCodeSet({
            name: values.name.trim(),
            code: values.shortCode.trim(),
            codes: toCodesMap(codes),
        });
    };
    var onRenameSet = function (values) {
        if (!selectedSet || selectedSet.version === undefined)
            return;
        setUpdateIntent("rename");
        updateRejectionCodeSet(selectedSet.id, {
            version: selectedSet.version,
            name: values.name.trim(),
            codes: toCodesMap(selectedSet.codes),
        });
    };
    var onConfirmDisableSet = function () {
        if (!selectedSet || selectedSet.version === undefined)
            return;
        disableRejectionCodeSet(selectedSet.id, selectedSet.version);
    };
    var onConfirmDeleteSet = function () {
        if (!selectedSet)
            return;
        setPendingDeleteSetId(selectedSet.id);
        deleteRejectionCodeSet(selectedSet.id);
    };
    var onAddCode = function (setId, values) {
        var _a;
        var target = sets.find(function (set) { return set.id === setId; });
        if (!target || target.version === undefined)
            return;
        var key = values.code.trim() || "__EMPTY__";
        setUpdateIntent("addCode");
        updateRejectionCodeSet(setId, {
            version: target.version,
            codes: __assign(__assign({}, toCodesMap(target.codes)), (_a = {}, _a[key] = formValuesToEntry(values), _a)),
        });
    };
    var onUpdateCode = function (setId, codeId, values) {
        var target = sets.find(function (set) { return set.id === setId; });
        if (!target || target.version === undefined)
            return;
        var codes = toCodesMap(target.codes);
        delete codes[codeId];
        var key = values.code.trim() || "__EMPTY__";
        codes[key] = formValuesToEntry(values);
        setUpdateIntent("updateCode");
        updateRejectionCodeSet(setId, { version: target.version, codes: codes });
    };
    var onRemoveCode = function (setId, codeId) {
        var target = sets.find(function (set) { return set.id === setId; });
        if (!target || target.version === undefined)
            return;
        var codes = toCodesMap(target.codes);
        delete codes[codeId];
        setUpdateIntent("removeCode");
        updateRejectionCodeSet(setId, { version: target.version, codes: codes });
    };
    return {
        loading: loadingRejectionCodeSets ||
            creatingRejectionCodeSet ||
            updatingRejectionCodeSet ||
            disablingRejectionCodeSet ||
            deletingRejectionCodeSet,
        seeToast: seeToast,
        setSeeToast: setSeeToast,
        sets: sets,
        filteredSets: paginatedSets,
        filter: filter,
        onFilterChange: onFilterChange,
        page: page,
        pageSize: PAGE_SIZE,
        totalItems: totalItems,
        onChangePage: setPage,
        selectedSet: selectedSet,
        onSelectSet: setSelectedSetId,
        newSetModalOpen: newSetModalOpen,
        onOpenNewSetModal: function () { return setNewSetModalOpen(true); },
        onCloseNewSetModal: function () { return setNewSetModalOpen(false); },
        onCreateSet: onCreateSet,
        renameModalOpen: renameModalOpen,
        onOpenRenameModal: function () { return setRenameModalOpen(true); },
        onCloseRenameModal: function () { return setRenameModalOpen(false); },
        onRenameSet: onRenameSet,
        confirmDisableModalOpen: confirmDisableModalOpen,
        onOpenConfirmDisableModal: function () { return setConfirmDisableModalOpen(true); },
        onCloseConfirmDisableModal: function () { return setConfirmDisableModalOpen(false); },
        onConfirmDisableSet: onConfirmDisableSet,
        disablingRejectionCodeSet: disablingRejectionCodeSet,
        confirmDeleteModalOpen: confirmDeleteModalOpen,
        onOpenConfirmDeleteModal: function () { return setConfirmDeleteModalOpen(true); },
        onCloseConfirmDeleteModal: function () { return setConfirmDeleteModalOpen(false); },
        onConfirmDeleteSet: onConfirmDeleteSet,
        deletingRejectionCodeSet: deletingRejectionCodeSet,
        onAddCode: onAddCode,
        onUpdateCode: onUpdateCode,
        onRemoveCode: onRemoveCode,
    };
};
